/**
 * useGracePeriodStatus Hook - Redux-based hook for grace period status
 *
 * ARCHITECTURAL PATTERN:
 * Pure Redux selector hook that reads grace period status from global state.
 * This hook does NOT handle polling - that is managed centrally by
 * useSubscriptionPolling() which should be called once at the app level.
 *
 * This ensures:
 * - Global accessibility across components
 * - Prevention of duplicate API calls
 * - Single polling timer for entire app (not per component)
 * - Better caching and state management
 * - Consistency with other subscription features
 *
 * USAGE:
 * const { status, loading, error, refresh } = useGracePeriodStatus();
 *
 * NOTE: Background polling is handled by useSubscriptionPolling() in MainLayout.
 * This hook only provides read access and manual refresh capability.
 */

import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/stateHooks';
import {
  fetchMyGracePeriodStatus,
  selectGracePeriodStatus,
  selectGracePeriodLoading,
  selectSubscriptionError,
} from '../redux/slices/subscriptionSliceModel';
import type { GracePeriodStatus } from '../services/subscription.service';

interface UseGracePeriodStatusReturn {
  status: GracePeriodStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useGracePeriodStatus = (): UseGracePeriodStatusReturn => {
  const dispatch = useAppDispatch();

  // Select from Redux store
  const status = useAppSelector(selectGracePeriodStatus);
  const loading = useAppSelector(selectGracePeriodLoading);
  const error = useAppSelector(selectSubscriptionError);

  // Refresh function for manual refresh
  const refresh = useCallback(() => {
    dispatch(fetchMyGracePeriodStatus());
  }, [dispatch]);

  return { status, loading, error, refresh };
};
